import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { supplierNcService } from '../../services/api';

const SEV_CLR = { Bassa: 'bg-slate-400 text-white', Media: 'bg-amber-500 text-white', Alta: 'bg-orange-600 text-white', Critica: 'bg-red-600 text-white' };
const CLASS_CLR = { A: 'bg-emerald-600 text-white', B: 'bg-sky-500 text-white', C: 'bg-amber-500 text-white', D: 'bg-red-600 text-white' };

/**
 * Widget riutilizzabile: mostra lo storico NC di un fornitore + la classe di valutazione annuale.
 * Da usare dentro la scheda anagrafica del fornitore.
 */
export const SupplierNcHistoryCard = ({ supplierId }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supplierId) return;
    setLoading(true);
    supplierNcService.bySupplier(supplierId, year)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento storico NC'))
      .finally(() => setLoading(false));
  }, [supplierId, year]);

  if (!supplierId) return null;

  return (
    <Card data-testid="supplier-nc-history-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          Storico Non Conformità Fornitore
        </CardTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Anno</Label>
            <Input type="number" className="w-24 h-8" value={year} onChange={(e) => setYear(Number(e.target.value) || currentYear)} data-testid="history-year" />
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Classe {data.year}</span>
              <Badge className={`${CLASS_CLR[data.class] || 'bg-slate-400 text-white'} px-3 py-1 text-base font-bold`} data-testid="supplier-class-badge">
                {data.class}
              </Badge>
              <span className="text-xs text-slate-500">Score {data.score}/100</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : !data || data.ncs.length === 0 ? (
          <p className="text-center text-slate-500 italic py-6">Nessuna NC fornitore per l&apos;anno {year}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  {['Codice NC', 'Data', 'Categoria', 'Gravità', 'Stato', 'Scadenza risposta', '€ Nota debito', ''].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.ncs.map(nc => (
                  <tr key={nc.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`hist-row-${nc.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">
                      <Link to={`/supplier-ncs/${nc.id}`} className="text-sky-700 hover:underline">{nc.supplier_nc_code}</Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{nc.issue_date}</td>
                    <td className="px-3 py-2 text-xs">{nc.defect_category_name || '—'}</td>
                    <td className="px-3 py-2"><Badge className={`${SEV_CLR[nc.severity] || ''} text-xs px-2 py-0.5`}>{nc.severity}</Badge></td>
                    <td className="px-3 py-2 text-xs">{nc.status}</td>
                    <td className="px-3 py-2 font-mono text-xs">{nc.response_deadline || '—'}</td>
                    <td className="px-3 py-2 text-xs">{nc.debit_note_amount ? <span className="text-violet-700 font-semibold">€ {nc.debit_note_amount.toFixed(2)}</span> : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/supplier-ncs/${nc.id}`} className="text-slate-400 hover:text-slate-700"><ExternalLink className="w-4 h-4 inline" /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
